
import React, { useState } from 'react';
import { ShoppingCart, X, Plus, Minus, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCart } from '@/hooks/useCart';
import { useNavigate } from 'react-router-dom';

export const FloatingCart = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { cartItems, updateCartItemQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  const itemCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const totalPrice = cartItems.reduce((total, item) => {
    const product = item.product;
    if (!product) return total;
    
    let price = product.incl_price || 0;
    if (product.is_special_discount && product.special_price) {
      price = product.special_price;
    }
    
    return total + (price * item.quantity);
  }, 0);

  if (itemCount === 0) return null;

  const handleViewCart = () => {
    navigate('/cart');
    setIsExpanded(false);
  };

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
    } else {
      updateCartItemQuantity(itemId, newQuantity);
    }
  };

  return (
    <>
      {/* Floating Cart Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          className="relative bg-gold-500 hover:bg-gold-600 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-105"
          size="lg"
        >
          <ShoppingCart className="h-6 w-6" />
          {itemCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {itemCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Expanded Cart Panel */}
      {isExpanded && (
        <div className="fixed bottom-24 right-6 z-50 w-80 max-w-[calc(100vw-3rem)] sm:w-96">
          <Card className="bg-cream-50 border-2 border-springbok-200 shadow-2xl">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Your Cart ({itemCount} items)
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="max-h-64 overflow-y-auto space-y-3 mb-4">
                {cartItems.slice(0, 3).map((item) => {
                  const product = item.product;
                  if (!product) return null;

                  let price = product.incl_price || 0;
                  if (product.is_special_discount && product.special_price) {
                    price = product.special_price;
                  }

                  const productImage = product.product_images && product.product_images.length > 0 
                    ? product.product_images[0].image_url 
                    : '/placeholder.svg';

                  return (
                    <div key={item.id} className="flex items-center gap-3 p-2 bg-white rounded-lg border">
                      <img
                        src={productImage}
                        alt={product.product_name}
                        className="w-12 h-12 object-cover rounded"
                        width={48}
                        height={48}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.product_name}</p>
                        <p className="text-xs text-gray-600">R{price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm font-medium px-2">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {cartItems.length > 3 && (
                  <p className="text-sm text-gray-600 text-center">
                    +{cartItems.length - 3} more items
                  </p>
                )}
              </div>
              
              <div className="border-t pt-3 space-y-3">
                <div className="flex justify-between items-center font-semibold">
                  <span>Total:</span>
                  <span>R{totalPrice.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={handleViewCart}
                  className="w-full bg-gold-500 hover:bg-gold-600"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Cart & Checkout
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Backdrop */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </>
  );
};
